import {
  BorderButtonStyled,
  EmptyStateStyled,
} from '@OnArk/styles/OrderedList.styled';
import { SubTitleStyled } from '@OnArk/styles/Text.styled';

import React, { ReactElement, useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { MaxWidthStyled } from '@hangar31/rc-layout';
import useModal, { WithModalProps } from '@hangar31/rc-modal';
import Icon from '@OnArk/SVGs/Icon';

const InputTypeButtonStyled = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 1.2rem;
    align-items: center;
    padding: 1.2rem 1.6rem;

    color: ${theme.colors.clickable};
    text-align: left;
    text-transform: capitalize;

    background: ${theme.colors.white};
    border-radius: ${theme.radius.md};

    i {
      display: block;
    }
    strong {
      display: block;
      margin: 0;

      font-weight: 400;
      font-size: 1.8rem;
    }
    small {
      display: block;
      margin: 0.2rem 0 0;

      color: ${theme.colors.grayDark};
      grid-column: 2;
    }
    &:hover {
      cursor: pointer;
      i {
        cursor: pointer;
      }
    }
  `
);

interface ButtonProps {
  insertAt: number;
  onClick?: (e: React.MouseEvent) => void;
  emptyState?: boolean;
  emptyLabel?: string;
  [key: string]: unknown;
}

/** Modal height that fits every option row without inner scroll. */
export function typePopupMaxHeight<T>(groups: TypeButtonGroup<T>[]): number {
  const rowCount = groups.reduce((t, g) => t + g.buttons.length + 1, 0);

  return Math.min(65 * rowCount + 15, 700);
}

function useTypePopup<T>({
  afterInsert,
  buttonProps,
  groups,
  sm,
}: {
  groups: TypeButtonGroup<T>[];
  afterInsert?: (insertAt: number, defaultObj: T) => void;
  sm?: boolean;
  buttonProps?: Record<string, any>;
}): (props: ButtonProps) => ReactElement {
  const insertAtIdx = useRef(0);

  function ButtonComponent({
    children,
    emptyLabel = 'Add Item',
    emptyState,
    insertAt,
    onClick,
    ...props
  }: React.PropsWithChildren<ButtonProps>) {
    const Button = emptyState ? EmptyStateStyled : BorderButtonStyled;
    const iconSize = sm ? 10 : 15;

    return (
      <Button
        withIcon
        // TEST HOOK (see BuilderItems.styled.tsx): #290 put `add-item` on the
        // form-options add button only, so e2e could never add a SECOND item
        // to a section — openBranchPicker/openFormFieldPicker select on it.
        data-e2e="add-item"
        iconSize={sm ? 1 : 1.5}
        outline={emptyState}
        type="button"
        onClick={e => {
          e.stopPropagation();
          if (onClick) onClick(e);
          insertAtIdx.current = insertAt;
        }}
        {...props}
        {...buttonProps}
      >
        <Icon
          name="ACTIONS_Add"
          right="0"
          size={`${emptyState ? iconSize + 5 : iconSize}`}
        />
        {emptyState && emptyLabel && (
          <span css={{ fontSize: sm ? '1.2rem' : 'inherit' }}>
            {emptyLabel}
          </span>
        )}
        {children}
      </Button>
    );
  }

  function InsertButton(props: ButtonProps): ReactElement {
    const [Modal] = useModal({
      ButtonComponent: ButtonComponent,
      buttonProps: props,
      maxHeight: typePopupMaxHeight(groups),
      ModalComponent: TypePopup,
      modalProps: {
        afterInsert,
        groups,
        insertAtIdx,
      },
      transitionOpacity: true,
    });

    return <Modal />;
  }

  return InsertButton;
}

interface PopupProps<T> {
  groups: TypeButtonGroup<T>[];
  afterInsert?: (insertAt: number, defaultObj: T) => void;
  insertAtIdx: React.MutableRefObject<number>;
}
export function TypePopup<T>({
  afterInsert,
  groups,
  insertAtIdx,
  modal,
}: WithModalProps & PopupProps<T>): ReactElement {
  const addInput = (defaultObj: T) => {
    if (afterInsert) afterInsert(insertAtIdx.current, defaultObj);
  };

  return (
    <div css={{ padding: '1.5rem' }}>
      {groups.map(group => (
        <React.Fragment key={group.label}>
          <MaxWidthStyled
            grid
            align="end"
            colGap={2}
            css={{ alignItems: 'stretch' }}
            rowGap={1}
          >
            <SubTitleStyled css={{ margin: 0 }}>{group.label}</SubTitleStyled>
            {group.buttons.map(button => (
              <InputTypeButtonStyled
                key={button.label}
                data-e2e="type-option"
                onClick={e => {
                  e.stopPropagation();
                  addInput(button.defaultObj);
                  modal?.close();
                }}
              >
                {button.icon && <Icon name={button.icon} size="30" />}
                <strong>{button.label || ''}</strong>
                {button.hint && <small>{button.hint}</small>}
              </InputTypeButtonStyled>
            ))}
          </MaxWidthStyled>
        </React.Fragment>
      ))}
    </div>
  );
}
export default useTypePopup;
